package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.BulkKeyOp
import dev.lingvo.shared.BulkKeysFilter
import dev.lingvo.shared.BulkKeysRequest
import dev.lingvo.shared.BulkKeysResponse
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.UpdateKeyRequest
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.automation.PretranslateConfirm
import dev.lingvo.site.components.automation.PretranslateScope
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArchiveRestoreIcon
import dev.lingvo.site.ds.LBulkActionBar
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSquareCheckIcon
import dev.lingvo.site.ds.LTagIcon
import dev.lingvo.site.ds.LTagsIcon
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Composable
internal fun KeysContent(
    /**
     * Opens the deleted-strings page, or null where there is nowhere to go.
     *
     * Null in the authority tests, which render this composable directly and have no router.
     */
    onOpenDeleted: (() -> Unit)? = null,
    foundation: KeysFoundationData,
    result: KeysSlice,
    authority: ProjectAuthority,
    authoritySubject: ProjectAuthoritySubject,
    isMutationCurrent: () -> Boolean = { authority.canEditStructure },
    slug: String,
    mutationClaim: ProjectActionClaim? = null,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    mutationTransport: KeysMutationTransport = apiKeysMutationTransport,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    search: String,
    onSearchChange: (String) -> Unit,
    fileId: String,
    onFileIdChange: (String) -> Unit,
    locale: String,
    onLocaleChange: (String) -> Unit,
    stateFilter: KeyStateFilter,
    onStateFilterChange: (KeyStateFilter) -> Unit,
    tag: String,
    onTagChange: (String) -> Unit,
    loadingMore: Boolean,
    /** A newer slice is in flight while these rows stay on screen; see the retained-refresh note. */
    refreshing: Boolean = false,
    /** A refresh that failed while usable rows are still on screen. */
    refreshError: String? = null,
    onLoadMore: () -> Unit,
    onOpenFiles: () -> Unit,
    /**
     * Reports a run this page started, so the page that owns the router can open its receipt.
     *
     * The run itself is priced and started here, beside the filter that scoped it — the estimate
     * has to describe the same set the button counted, and only this composable knows what that set
     * is. Null in the authority tests, and null for any role the server would refuse: the control is
     * absent rather than disabled, which is what principle 6 asks for.
     *
     * The id may name a run that was already going: `409 job_already_running` is reported through
     * the same callback, because the work the reader asked for is happening either way. [strings] is
     * the number the pressed control stated, so the receipt reads as the answer to what was asked.
     */
    onPretranslateStarted: ((jobId: String, strings: Int) -> Unit)? = null,
    /** Opens one string's permalink page. Null in the authority tests, which have no router. */
    onOpenPermalink: ((KeyRow) -> Unit)? = null,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    val startMutation: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }.let {}
    }
    val canEditStructure = authority.canEditStructure
    val actionStateKey = authoritySubject
    var selected by remember(actionStateKey) { mutableStateOf<Set<String>>(emptySet()) }
    // **The second step, and it is a step.** "Select all 1,204 matching" is a different decision
    // from ticking the rows on screen — it acts on keys the reader has not seen — so it is a
    // separate press, and it is forgotten the moment the selection or the filters change.
    var selectAllMatching by remember(actionStateKey) { mutableStateOf(false) }
    var bulkTag by remember(actionStateKey) { mutableStateOf("") }
    var draft by remember(actionStateKey) {
        mutableStateOf(KeyDraft(fileId = foundation.files.firstOrNull()?.id.orEmpty()))
    }
    var editing by remember(actionStateKey) { mutableStateOf<KeyRow?>(null) }
    var deleting by remember(actionStateKey) { mutableStateOf<List<KeyRow>>(emptyList()) }
    var adding by remember(actionStateKey) { mutableStateOf(false) }
    var bulkDeleteOpen by remember(actionStateKey) { mutableStateOf(false) }
    // The run currently being priced, or null. It carries its own scope rather than reading the
    // filters when the dialog draws: the reader can change a filter with the estimate open, and the
    // run that was priced has to be the run that starts.
    var pretranslating by remember(actionStateKey) { mutableStateOf<KeysPretranslateAsk?>(null) }
    var busy by remember(actionStateKey) { mutableStateOf(false) }
    var previouslyCouldEdit by remember(foundation.project.id) { mutableStateOf(canEditStructure) }
    val filesById = foundation.files.associateBy(FileDto::id)
    val targetLocales = foundation.targetLocales
    // Grepping `/translate/` across this module finds hits in ReadinessBand, Index, Qa,
    // ProjectOverviewModel and strings/[keyId] — none in this file. The name cell is the row's
    // subject and the way into the work; before this the list could find a key and not open it.
    val linkLocale = locale.ifBlank { targetLocales.firstOrNull() }
    val filtersApplied = search.isNotBlank() || fileId.isNotBlank() ||
        stateFilter != KeyStateFilter.ANY || tag.isNotBlank()
    // **The empty state below carries the primary itself.** With no files, the header offered
    // "Add file" and the empty state offered "Add file" — two accent buttons, same words, same
    // handler, one destination; the same again for "Add key" on a project with files and no keys.
    // `Index.kt` guards its own header with `showsOnlyEmptyState` for exactly this reason and the
    // guard was never applied here. Filtered-to-nothing is not one of these cases: that empty state
    // offers "Clear filters", which is a different act from adding a key.
    val bodyOffersTheSamePrimary =
        foundation.files.isEmpty() || (result.total == 0 && !filtersApplied)

    LaunchedEffect(canEditStructure) {
        if (previouslyCouldEdit && !canEditStructure) {
            adding = false
            editing = null
            deleting = emptyList()
            selected = emptySet()
            selectAllMatching = false
            (document.getElementById("project-keys-readonly-$slug") as? HTMLElement)?.focus()
        }
        previouslyCouldEdit = canEditStructure
    }

    fun mutate(
        success: String,
        failure: String,
        after: () -> Unit = {},
        block: suspend (ProjectActionClaim) -> Unit,
    ) {
        val claim = mutationClaim ?: return
        if (
            !canEditStructure ||
            !isMutationCurrent() ||
            !claim.remainsAuthorized(currentRoute(), currentMemberships())
        ) {
            return
        }
        startMutation action@{
            if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) return@action
            busy = true
            try {
                block(claim)
                if (
                    !isMutationCurrent() ||
                    !claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    return@action
                }
                after()
                notify(success, LToastVariant.SUCCESS, 4_000)
                onChanged()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (error: Throwable) {
                if (
                    isMutationCurrent() &&
                    claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    notify(error.safeKeysMessage(failure), LToastVariant.DANGER, TOAST_UNTIL_DISMISSED)
                }
            } finally {
                if (
                    isMutationCurrent() &&
                    claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    busy = false
                }
            }
        }
    }

    /**
     * One request per selected row, with **every** row attempted.
     *
     * The loop this replaces sat inside a single [mutate] call and stopped at its first failure. A
     * bulk delete that failed on row three left rows one and two gone from the database and still
     * drawn in the table: `after` and `onChanged` never ran, the toast said none of it had worked,
     * and the obvious retry re-issued deletes for ids the server had already dropped.
     *
     * Authority loss still stops everything at once. That is a cancellation rather than a failure,
     * and publishing anything after it is precisely what `requestProjectAction` exists to prevent.
     */
    fun mutateEach(
        targets: List<KeyRow>,
        allSucceeded: (Int) -> String,
        partial: (done: Int, failed: Int) -> String,
        allFailed: String,
        after: (Set<String>) -> Unit = {},
        block: suspend (ProjectActionClaim, KeyRow) -> Unit,
    ) {
        val claim = mutationClaim ?: return
        if (
            targets.isEmpty() ||
            !canEditStructure ||
            !isMutationCurrent() ||
            !claim.remainsAuthorized(currentRoute(), currentMemberships())
        ) {
            return
        }
        startMutation action@{
            if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) return@action
            busy = true
            val done = mutableSetOf<String>()
            var lastFailure: Throwable? = null
            try {
                targets.forEach { row ->
                    try {
                        block(claim, row)
                        done += row.key.id
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (error: Throwable) {
                        lastFailure = error
                    }
                }
                if (
                    !isMutationCurrent() ||
                    !claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    return@action
                }
                after(done)
                val failed = targets.size - done.size
                when {
                    failed == 0 -> notify(allSucceeded(done.size), LToastVariant.SUCCESS, 4_000)
                    done.isEmpty() -> notify(
                        lastFailure?.safeKeysMessage(allFailed) ?: allFailed,
                        LToastVariant.DANGER,
                        TOAST_UNTIL_DISMISSED,
                    )
                    // Neither a success nor a failure, and said as neither. It stays up longer
                    // because it is the only place the split is reported.
                    else -> notify(partial(done.size, failed), LToastVariant.WARNING, 8_000)
                }
                // A partial result changed the table too, and rows the server has already dropped
                // must stop being drawn as though they were still there.
                if (done.isNotEmpty()) onChanged()
            } finally {
                if (
                    isMutationCurrent() &&
                    claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    busy = false
                }
            }
        }
    }

    /**
     * One decision about many keys, in one request and one transaction.
     *
     * **Either the ticked ids or the filter, never both.** `BulkKeysRequest` refuses a request that
     * carries two different answers to "which keys", and the filter form is what "Select all
     * {total} matching" means: the server resolves it through the identical predicate builder
     * `GET /keys` uses, so the set on screen and the set that changes cannot disagree.
     *
     * The receipt states what the server actually did. `applied` counts rows the database changed,
     * so tagging forty keys that already carry the tag reports zero rather than claiming forty.
     */
    fun bulkMutate(
        op: BulkKeyOp,
        opTag: String? = null,
        failure: String,
        after: () -> Unit = {},
    ) {
        val claim = mutationClaim ?: return
        if (
            !canEditStructure ||
            !isMutationCurrent() ||
            !claim.remainsAuthorized(currentRoute(), currentMemberships())
        ) {
            return
        }
        val request = if (selectAllMatching) {
            BulkKeysRequest(
                op = op,
                filter = BulkKeysFilter(
                    fileId = fileId.ifBlank { null },
                    tag = tag.trim().ifBlank { null },
                    search = search.trim().ifBlank { null },
                    state = stateFilter.apiState,
                    locale = locale.ifBlank { null },
                ),
                tag = opTag,
            )
        } else {
            BulkKeysRequest(op = op, keyIds = selected.toList(), tag = opTag)
        }
        if (request.filter == null && request.keyIds.isEmpty()) return
        startMutation action@{
            if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) return@action
            busy = true
            try {
                val response = requestProjectAction(claim, currentRoute, currentMemberships) {
                    mutationTransport.bulk(slug, request)
                }
                if (
                    !isMutationCurrent() ||
                    !claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    return@action
                }
                selected = emptySet()
                selectAllMatching = false
                after()
                notify(
                    bulkReceipt(op, response),
                    if (response.applied == 0) LToastVariant.WARNING else LToastVariant.SUCCESS,
                    // A receipt that names skipped rows is the only place that split is reported,
                    // so it stays up long enough to be read.
                    if (response.skipped.isEmpty()) 4_000 else 8_000,
                )
                onChanged()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (error: Throwable) {
                if (
                    isMutationCurrent() &&
                    claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    notify(error.safeKeysMessage(failure), LToastVariant.DANGER, TOAST_UNTIL_DISMISSED)
                }
            } finally {
                if (
                    isMutationCurrent() &&
                    claim.remainsAuthorized(currentRoute(), currentMemberships())
                ) {
                    busy = false
                }
            }
        }
    }

    val openAdd: () -> Unit = {
        if (canEditStructure && isMutationCurrent()) {
            if (foundation.files.isEmpty()) {
                onOpenFiles()
            } else {
                draft = KeyDraft(fileId = foundation.files.first().id)
                adding = true
            }
        }
    }
    PageHeader(
        title = t("app.projects.slug.keys.keys", "Keys"),
        subtitle = trf(
            "app.projects.slug.keys.subtitle_keys_across_files",
            "{keys} across {files}.",
            "keys" to resultCountLabel(result.total),
            "files" to tPlural(
                "app.projects.slug.keys.count.file",
                foundation.files.size,
                "{count} file",
                "{count} files",
            ),
        ),
    ) {
        if (canEditStructure) {
            // The way back from a delete, for the role that can perform one. Navigation arrives as
            // a callback rather than a `Link` for the same reason `openAdd` does: this composable is
            // rendered directly by the authority tests, outside any `@Page`, and `PageContext` is
            // only valid inside one.
            onOpenDeleted?.let {
                LButton(
                    t("app.projects.slug.keys.deleted_strings", "Deleted keys"),
                    it,
                    variant = LButtonVariant.GHOST,
                    leadingIcon = { LArchiveRestoreIcon() },
                )
            }
            if (!bodyOffersTheSamePrimary) {
                LButton(t("app.projects.slug.keys.add_key", "Add key"), openAdd, leadingIcon = { LPlusIcon() })
            }
        }
    }
    if (!canEditStructure) {
        // Focusable, so a demotion mid-session can land the reader on the explanation. `Glossary.kt`
        // and the editor's QA page both do this; here focus went to the search field instead, which
        // puts a demoted reader in a text box with nothing announcing what changed.
        Div(attrs = {
            id("project-keys-readonly-$slug")
            attr("tabindex", "-1")
        }) {
            LAlert(
                title = t("app.projects.slug.keys.project_structure_is_managed_by_an", "Project structure is managed by an owner or admin"),
                description = t("app.projects.slug.keys.you_can_search_and_open_every", "You can search and open every key, while project files, source strings, tags, and metadata stay protected."),
                variant = LAlertVariant.INFO,
            )
        }
    }
    // **Retained refresh, not a teardown.** Changing a filter used to put the whole page back into
    // its loading state — which unmounts this filter bar, so the search field a translator was
    // typing in disappeared from under them and focus fell to `body` after every debounce. The rows
    // stay, the field keeps focus, and the fact that newer results are coming is said here.
    refreshError?.let {
        LAlert(
            title = t("app.projects.slug.keys.couldnt_refresh_these_keys", "Couldn't refresh these keys"),
            description = it,
            variant = LAlertVariant.WARNING,
            live = true,
        )
    }
    Div(
        Modifier
            .attr("data-keys-refreshing", refreshing.toString())
            .attr("aria-live", "polite")
            .attr("aria-atomic", "true")
            .toAttrs(),
    ) {
        if (refreshing) {
            Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                Text(t("app.projects.slug.keys.updating_results", "Updating results"))
            }
        }
    }
    KeysFilterBar(
        slug = slug,
        search = search,
        onSearchChange = onSearchChange,
        files = foundation.files,
        fileId = fileId,
        onFileIdChange = onFileIdChange,
        targetLocales = targetLocales,
        locale = locale,
        onLocaleChange = onLocaleChange,
        stateFilter = stateFilter,
        onStateFilterChange = onStateFilterChange,
        tag = tag,
        onTagChange = onTagChange,
        shown = result.rows.size,
        total = result.total,
        onClearAll = {
            onSearchChange("")
            onFileIdChange("")
            onStateFilterChange(KeyStateFilter.ANY)
            onTagChange("")
        },
        // The run this filter makes obvious, beside the filter that scoped it. Offered only where
        // it states a real count against a named language and the server would allow it.
        //
        // **And only where the count is one the run can actually cover.** `MtPretranslateRequest`
        // understands a file, a tag and a set of ids; it has no search predicate. With a search
        // applied, "Pre-translate 12 untranslated in German" would have started a run over every
        // untranslated string in German — a hundred times the number on the button, priced and
        // charged. The control is absent while a search is on rather than quietly wrong.
        onPretranslateFiltered = onPretranslateStarted
            ?.takeIf {
                stateFilter == KeyStateFilter.UNTRANSLATED &&
                    locale.isNotBlank() &&
                    result.total > 0 &&
                    search.isBlank()
            }
            ?.let {
                {
                    pretranslating = keysPretranslateAsk(
                        locale = locale,
                        fileId = fileId,
                        tag = tag,
                        keyIds = emptyList(),
                        strings = result.total,
                    )
                }
            },
    )
    // **One bar, whatever the selection covers.** The card this replaces appeared only when a row
    // was ticked and pushed the table down under the reader's cursor; the bar is a fixed strip that
    // holds its last count through its own exit, so it does not count itself down to zero on the
    // way out.
    if (canEditStructure) {
        val selectionCount = if (selectAllMatching) result.total else selected.size
        val loadedAllSelected = result.rows.isNotEmpty() && result.rows.all { it.key.id in selected }
        LBulkActionBar(
            selectedCount = selectionCount,
            onClear = {
                selected = emptySet()
                selectAllMatching = false
            },
            // `{amount}`, not `{count}`: `tPlural` substitutes the raw integer for `{count}`, and
            // "1204 keys selected" is the one place on this page a four-digit number would lose its
            // grouping. The plural form is still chosen from the real count.
            label = tPlural(
                "app.projects.slug.keys.count.selected",
                selectionCount,
                "{amount} key selected",
                "{amount} keys selected",
                "amount" to formatCount(selectionCount),
            ),
            note = if (selectAllMatching) {
                tr(
                    "app.projects.slug.keys.selection_covers_every_matching_key",
                    "Every key matching these filters, including ones not loaded yet.",
                )
            } else {
                null
            },
            returnFocusToId = keysSelectAllHeaderId(slug),
        ) {
            LTextField(
                value = bulkTag,
                onValueChange = { bulkTag = it },
                modifier = Modifier.width(200.px),
                // Not "Tag": the filter bar eight pixels above already has a field by that name,
                // and two controls called Tag on one screen — one that narrows the list, one that
                // changes four hundred keys — is the pair somebody types into the wrong half of.
                label = t("app.projects.slug.keys.tag_to_apply", "Tag to apply"),
                placeholder = "checkout",
            )
            LButton(
                t("app.projects.slug.keys.add_tag", "Add tag"),
                {
                    bulkMutate(
                        op = BulkKeyOp.ADD_TAG,
                        opTag = bulkTag.trim(),
                        failure = tr("app.projects.slug.keys.couldnt_add_the_tag_retry", "Couldn't add the tag. Retry?"),
                        after = { bulkTag = "" },
                    )
                },
                enabled = bulkTag.isNotBlank() && !busy,
                loading = busy,
                variant = LButtonVariant.SECONDARY,
                leadingIcon = { LTagIcon() },
            )
            LButton(
                t("app.projects.slug.keys.remove_tag", "Remove tag"),
                {
                    bulkMutate(
                        op = BulkKeyOp.REMOVE_TAG,
                        opTag = bulkTag.trim(),
                        failure = tr("app.projects.slug.keys.couldnt_remove_the_tag_retry", "Couldn't remove the tag. Retry?"),
                        after = { bulkTag = "" },
                    )
                },
                enabled = bulkTag.isNotBlank() && !busy,
                variant = LButtonVariant.GHOST,
                leadingIcon = { LTagsIcon() },
            )
            // Absent, not disabled, for a role the server would refuse, and absent while there is
            // no language to run against — a machine run needs a target locale to write into.
            if (onPretranslateStarted != null && locale.isNotBlank() && !selectAllMatching) {
                LButton(
                    tPlural(
                        "app.projects.slug.keys.count.pretranslate_selected",
                        selected.size,
                        "Pre-translate {count} selected",
                        "Pre-translate {count} selected",
                    ),
                    {
                        pretranslating = keysPretranslateAsk(
                            locale = locale,
                            fileId = fileId,
                            tag = tag,
                            keyIds = selected.toList(),
                            strings = selected.size,
                        )
                    },
                    enabled = !busy,
                    variant = LButtonVariant.SECONDARY,
                    leadingIcon = { LWandSparklesIcon() },
                )
            }
            LButton(
                t("app.projects.slug.keys.delete_selected", "Delete selected"),
                { bulkDeleteOpen = true },
                variant = LButtonVariant.DANGER,
                enabled = !busy,
                leadingIcon = { LTrashIcon() },
            )
            // The second step. It appears only once every loaded row is ticked and the server has
            // more than the page holds — before that it would offer to select rows the reader can
            // already see and has deliberately left alone.
            if (loadedAllSelected && !selectAllMatching && result.total > result.rows.size) {
                LButton(
                    trf(
                        "app.projects.slug.keys.select_all_matching",
                        "Select all {total} matching",
                        "total" to formatCount(result.total),
                    ),
                    { selectAllMatching = true },
                    variant = LButtonVariant.GHOST,
                    enabled = !busy,
                    leadingIcon = { LSquareCheckIcon() },
                )
            }
        }
    }
    when {
        foundation.files.isEmpty() -> LEmptyState(
            title = if (canEditStructure) {
                t("app.projects.slug.keys.add_a_localization_file", "Add a localization file")
            } else {
                tr("app.projects.slug.keys.no_localization_files_yet", "No localization files yet")
            },
            description = if (canEditStructure) {
                tr("app.projects.slug.keys.keys_belong_to_files_add_the", "Keys belong to files. Add the first project file before creating a source string.")
            } else {
                tr("app.projects.slug.keys.an_owner_or_admin_needs_to", "An owner or admin needs to add the first project file before source strings appear here.")
            },
            icon = LEmptyStateIcon.KEY,
        ) {
            if (canEditStructure) LButton(t("app.projects.slug.keys.add_file", "Add file"), onOpenFiles)
        }
        result.total == 0 && !filtersApplied -> LEmptyState(
            title = if (canEditStructure) t("app.projects.slug.keys.add_your_first_key", "Add your first key") else tr("app.projects.slug.keys.no_keys_yet", "No keys yet"),
            description = if (canEditStructure) {
                tr("app.projects.slug.keys.create_a_source_string_to_start", "Create a source string to start translating this project.")
            } else {
                tr("app.projects.slug.keys.an_owner_or_admin_needs_to_2", "An owner or admin needs to create the first source string before translation can begin.")
            },
            icon = LEmptyStateIcon.KEY,
        ) {
            if (canEditStructure) LButton(t("app.projects.slug.keys.add_key", "Add key"), openAdd)
        }
        result.total == 0 -> LEmptyState(
            title = t("app.projects.slug.keys.no_keys_match_these_filters", "No keys match these filters"),
            description = t("app.projects.slug.keys.try_another_search_or_clear_the", "Try another search, or clear the filters."),
            icon = LEmptyStateIcon.SEARCH,
        ) {
            LButton(t("app.projects.slug.keys.clear_filters", "Clear filters"), {
                onSearchChange("")
                onFileIdChange("")
                onStateFilterChange(KeyStateFilter.ANY)
                onTagChange("")
            }, variant = LButtonVariant.SECONDARY)
        }
        else -> {
            KeysTable(
                rows = result.rows,
                targetLocales = targetLocales,
                coverage = result.coverage,
                filesById = filesById,
                total = result.total,
                selected = selected,
                selectAllHeaderId = keysSelectAllHeaderId(slug),
                sourceLocale = foundation.project.sourceLocale,
                slug = slug,
                linkLocale = linkLocale,
                canEditStructure = canEditStructure,
                onSelected = { id, checked ->
                    if (canEditStructure && isMutationCurrent()) {
                        selected = if (checked) selected + id else selected - id
                        // Ticking or unticking one row is a statement about that row, and it retires
                        // the claim that every matching key was chosen.
                        selectAllMatching = false
                    }
                },
                onSelectAll = { checked ->
                    if (canEditStructure && isMutationCurrent()) {
                        val loadedIds = result.rows.map { it.key.id }.toSet()
                        selected = if (checked) selected + loadedIds else selected - loadedIds
                        selectAllMatching = false
                    }
                },
                onEdit = { row ->
                    if (canEditStructure && isMutationCurrent()) {
                        editing = row
                        draft = KeyDraft(
                            fileId = row.key.fileId,
                            name = row.key.name,
                            context = row.key.context.orEmpty(),
                            maxLength = row.key.maxLength?.toString().orEmpty(),
                            tags = row.key.tags.joinToString(", "),
                            isPlural = row.key.isPlural,
                            // Carried so the edit dialog reflects what is stored. The update request does
                            // not send source forms today, but a draft that silently forgets them is a
                            // trap for whoever wires that up next.
                            sourcePluralForms = row.source.pluralForms.orEmpty(),
                        )
                    }
                },
                onDelete = { if (canEditStructure && isMutationCurrent()) deleting = listOf(it) },
                busy = busy,
                onOpenPermalink = onOpenPermalink,
            )
            if (result.nextCursor != null) {
                LButton(
                    t("app.projects.slug.keys.load_more", "Load more"),
                    onLoadMore,
                    variant = LButtonVariant.SECONDARY,
                    enabled = !loadingMore,
                    loading = loadingMore,
                )
            }
        }
    }
    if (canEditStructure) {
        KeyModal(adding, null, foundation.files, draft, foundation.project.sourceLocale, { draft = it }, { adding = false }, busy) {
            val request = draft.createRequest()
            mutate(
                tr("app.projects.slug.keys.key_added", "Key added"),
                tr("app.projects.slug.keys.couldnt_add_this_key_retry", "Couldn't add this key. Retry?"),
                after = { adding = false },
            ) { claim ->
                requestProjectAction(claim, currentRoute, currentMemberships) {
                    mutationTransport.createKey(slug, request)
                }
            }
        }
        KeyModal(editing != null, editing, foundation.files, draft, foundation.project.sourceLocale, { draft = it }, { editing = null }, busy) {
            val row = editing ?: return@KeyModal
            mutate(
                tr("app.projects.slug.keys.key_saved", "Key saved"),
                tr("app.projects.slug.keys.couldnt_save_this_key_retry", "Couldn't save this key. Retry?"),
                after = { editing = null },
            ) { claim ->
                requestProjectAction(claim, currentRoute, currentMemberships) {
                    mutationTransport.updateKey(slug, row.key.id, draft.updateRequest())
                }
            }
        }
        // **`LConfirmDialog`, not a hand-rolled pair of buttons.** The consequence — that the
        // translations survive and the key can be brought back — is the whole reason this is a soft
        // delete, and it belongs in the slot the dialog reserves for it rather than as body prose a
        // reader skims past on the way to the red button.
        LConfirmDialog(
            open = deleting.isNotEmpty(),
            title = trf(
                "app.projects.slug.keys.delete_named_key",
                "Delete {name}?",
                "name" to deleting.firstOrNull()?.key?.name.orEmpty(),
            ),
            confirmLabel = tr("app.projects.slug.keys.delete_key", "Delete key"),
            onConfirm = {
                val targets = deleting
                mutateEach(
                    targets = targets,
                    allSucceeded = { count -> deletedLabel(count) },
                    partial = { done, failed ->
                        deletedLabel(done) + " · " + tPlural(
                            "app.projects.slug.keys.count.delete_failed",
                            failed,
                            "{count} could not be deleted",
                            "{count} could not be deleted",
                        )
                    },
                    allFailed = tr(
                        "app.projects.slug.keys.couldnt_delete_this_key_retry",
                        "Couldn't delete this key. Retry?",
                    ),
                    after = { done ->
                        selected = selected - done
                        // Left open when nothing went through, so a retry is where the reader
                        // already is.
                        if (done.isNotEmpty()) deleting = emptyList()
                    },
                ) { claim, row ->
                    requestProjectAction(claim, currentRoute, currentMemberships) {
                        mutationTransport.deleteKey(slug, row.key.id)
                    }
                }
            },
            onCancel = { if (!busy) deleting = emptyList() },
            // Both halves, because `LConfirmDialog` draws the consequence panel only when it has a
            // heading as well as a sentence — passing one alone renders neither.
            consequenceTitle = tr("app.projects.slug.keys.nothing_is_destroyed", "Nothing is destroyed"),
            consequence = tr(
                "app.projects.slug.keys.its_translations_are_kept_with_it",
                "Its translations are kept with it, and you can bring it back from Deleted keys.",
            ),
            busy = busy,
        )
        LConfirmDialog(
            open = bulkDeleteOpen,
            title = if (selectAllMatching) {
                trf(
                    "app.projects.slug.keys.delete_every_matching_key",
                    "Delete all {total} matching keys?",
                    "total" to formatCount(result.total),
                )
            } else {
                tPlural(
                    "app.projects.slug.keys.count.delete_selected_keys",
                    selected.size,
                    "Delete the {count} selected key?",
                    "Delete all {count} selected keys?",
                )
            },
            confirmLabel = tr("app.projects.slug.keys.delete_keys", "Delete keys"),
            onConfirm = {
                bulkMutate(
                    op = BulkKeyOp.DELETE,
                    failure = tr(
                        "app.projects.slug.keys.couldnt_delete_any_of_the_selected",
                        "Couldn't delete any of the selected keys. Retry?",
                    ),
                    after = { bulkDeleteOpen = false },
                )
            },
            onCancel = { if (!busy) bulkDeleteOpen = false },
            consequenceTitle = tr("app.projects.slug.keys.nothing_is_destroyed", "Nothing is destroyed"),
            consequence = tr(
                "app.projects.slug.keys.their_translations_are_kept_with_them",
                "Their translations are kept with them, and you can bring them back from Deleted keys.",
            ),
            busy = busy,
        )
        // The one price this page can show, for whichever scope asked for it — the filter bar's
        // "Pre-translate {n} untranslated in German" or the selection strip's "Pre-translate 12
        // selected". Both hand over a scope rather than a count, so the estimate and the run are
        // built from the same object and cannot describe different sets.
        pretranslating?.let { ask ->
            PretranslateConfirm(
                open = true,
                slug = slug,
                locales = listOf(ask.locale),
                scope = ask.scope,
                onStarted = { jobId ->
                    // A fresh run and the one a 409 named arrive here identically, which is the
                    // point: the reader asked for work that is now happening, and the page that
                    // owns the router opens its receipt.
                    onPretranslateStarted?.invoke(jobId, ask.strings)
                    selected = emptySet()
                    selectAllMatching = false
                },
                onClose = { pretranslating = null },
            )
        }
    }
}

/**
 * A machine run the keys page is about to price: one language, and the scope the filters describe.
 */
internal data class KeysPretranslateAsk(
    val locale: String,
    val scope: PretranslateScope,
    /** What the control that opened this said it would run over, for the receipt to echo. */
    val strings: Int,
)

/**
 * The run the current filters describe, as the server understands scope.
 *
 * **Ticked rows win, and they win alone.** A selection is an explicit answer to "which strings", so
 * mixing it with the file and tag pickers would silently narrow what the reader pointed at — the
 * deep link this replaces made the same choice, and it is the one that cannot surprise anybody.
 *
 * `onlyEmpty` stays true in both shapes. A machine run over a string somebody has already
 * translated overwrites human work, and neither of these controls says that is what it does.
 */
internal fun keysPretranslateAsk(
    locale: String,
    fileId: String,
    tag: String,
    keyIds: List<String>,
    strings: Int,
): KeysPretranslateAsk = KeysPretranslateAsk(
    locale = locale,
    strings = strings,
    scope = PretranslateScope(
        onlyEmpty = true,
        tag = tag.trim().takeIf { it.isNotBlank() && keyIds.isEmpty() },
        fileId = fileId.takeIf { it.isNotBlank() && keyIds.isEmpty() },
        keyIds = keyIds,
    ),
)

/**
 * The id the select-all header cell carries, and the element the bulk bar returns focus to.
 *
 * Without it, clearing a selection destroys the control that had focus and strands a keyboard
 * reader on `<body>` at the top of the document.
 */
internal fun keysSelectAllHeaderId(slug: String): String = "project-keys-select-all-$slug"

/**
 * What a bulk request actually did, said as one sentence.
 *
 * **`applied`, never the number the caller sent.** A request to tag forty keys that finds
 * thirty-eight already tagged changed two rows, and a receipt claiming forty is a receipt for work
 * that never happened. Skipped rows are counted rather than listed: they are races — somebody else
 * deleted or renamed a key between the page loading and the button being pressed — and forty key
 * names in a toast is not a thing anybody reads.
 */
internal fun bulkReceipt(op: BulkKeyOp, response: BulkKeysResponse): String {
    val applied = when (op) {
        BulkKeyOp.ADD_TAG -> tPlural(
            "app.projects.slug.keys.count.bulk_tagged",
            response.applied,
            "Tag added to {count} key",
            "Tag added to {count} keys",
        )
        BulkKeyOp.REMOVE_TAG -> tPlural(
            "app.projects.slug.keys.count.bulk_untagged",
            response.applied,
            "Tag removed from {count} key",
            "Tag removed from {count} keys",
        )
        BulkKeyOp.DELETE -> tPlural(
            "app.projects.slug.keys.count.bulk_deleted",
            response.applied,
            "{count} key deleted",
            "{count} keys deleted",
        )
        BulkKeyOp.RESTORE -> tPlural(
            "app.projects.slug.keys.count.bulk_restored",
            response.applied,
            "{count} key restored",
            "{count} keys restored",
        )
    }
    if (response.skipped.isEmpty()) return applied
    return applied + " · " + tPlural(
        "app.projects.slug.keys.count.bulk_left_alone",
        response.skipped.size,
        "{count} was already in that state",
        "{count} were already in that state",
    )
}



// `if (n == 1) "key" else "keys"` is English's rule wearing a general name, on the screens of a
// product whose argument is that plurals are real. `tPlural` asks the browser which form the
// reader's language selects.
private fun resultCountLabel(total: Int): String =
    tPlural("app.projects.slug.keys.count.key", total, "{count} key", "{count} keys")

private fun deletedLabel(count: Int): String = tPlural(
    "app.projects.slug.keys.count.deleted",
    count,
    "{count} key deleted",
    "{count} keys deleted",
)


internal fun Throwable.safeKeysMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback
